import { Alert } from "@/components/Alert";
import { Button } from "@/components/Button";
import { useGetBilling } from "@/requests/hooks/user";
import { faTimesCircle } from "@fortawesome/pro-duotone-svg-icons";
import { faCaretRight } from "@fortawesome/pro-solid-svg-icons";
import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { VariantProps, tv } from "tailwind-variants";

const styles = tv({
  slots: {
    base: "-mb-[150px] mt-[190px] flex w-full items-center justify-center transition-height md:mb-[unset] md:mt-[unset] md:flex md:px-80",
    alertBackground: "flex-grow md:bg-white",
    body: "flex flex-grow flex-col items-center justify-between gap-20 px-10 pb-10 md:flex-row md:pb-[unset]",
    buttons: "flex max-h-40 items-center gap-20",
  },
  variants: {
    variant: {
      success: {
        base: "md:bg-green/20",
      },
      error: {
        base: "md:bg-red/30",
      },
      warning: {
        base: "md:bg-orange/25",
      },
    },
    collapsed: {
      false: { base: "h-80" },
      true: { base: "h-[0px]" },
    },
  },
});

const AlertBanner = () => {
  const { t } = useTranslation();
  const [variant, setVariant] = useState<
    VariantProps<typeof styles>["variant"] | undefined
  >();
  const [collapseBanner, setCollapseBanner] = useState<boolean>(true);
  const { base, alertBackground, body, buttons } = styles({
    variant,
    collapsed: collapseBanner,
  });

  const { isFetching: isFetchingBilling, data: billing } = useGetBilling();

  const dismissNotification = () => {
    setCollapseBanner(true);
  };

  const getTransKey = () => {
    switch (variant) {
      case "error":
        return "components.alertBanner.billing.chargeDeclined";
      case "warning":
        return "components.alertBanner.billing.approvalRequired";
    }
  };

  const renderCtaButtons = () => (
    <div className={buttons()}>
      <Button
        as="link"
        variant="link"
        to="/settings/billing"
        trackingId={{
          eventName: "control-panel.billing.notification-link",
          additionalProperties: {
            type: variant === "error" ? "charge declined" : "approval needed",
          },
        }}
        icon={faCaretRight}
        iconPosition="end"
        iconClassName="w-20 h-20">
        {t("components.alertBanner.billing.linkCta")}
      </Button>
      <Button
        onClick={dismissNotification}
        variant="secondary-outline"
        icon={faTimesCircle}
        iconPosition="end"
        trackingId="control-panel.billing.notification-dismiss">
        {t("components.alertBanner.billing.dismiss")}
      </Button>
    </div>
  );

  const renderBody = () => {
    const transKey = getTransKey();

    return (
      <div className={body()}>
        <span>
          <Trans i18nKey={transKey}>
            <b>zero</b>
            one
          </Trans>
        </span>
        {renderCtaButtons()}
      </div>
    );
  };

  const renderAlert = () => (
    <div className={base()} data-testid="alert-banner">
      <div className={alertBackground()}>
        <Alert variant={variant} titleSlot={renderBody()} rounded="unset" />
      </div>
    </div>
  );

  const renderNotification = () => {
    const { subscription: currentSubscription, upcomingSubscription } =
      billing!;
    const subscription = currentSubscription || upcomingSubscription;

    if (upcomingSubscription?.status === "DECLINED") {
      if (variant !== "error") {
        setVariant("error");
        setCollapseBanner(false);
      }

      return renderAlert();
    }

    if (!subscription) return null;

    const { status, confirmationUrl } = subscription;

    if (confirmationUrl && status === "PENDING") {
      if (variant !== "warning") {
        setVariant("warning");
        setCollapseBanner(false);
      }

      return renderAlert();
    }

    return null;
  };

  if (isFetchingBilling || !billing) return null;

  return renderNotification();
};

export default AlertBanner;
